import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const AdminFormModal = ({
  open = true,
  title,
  subtitle,
  onClose,
  children,
  size = "lg",
}) => {
  useEffect(() => {
    if (open) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previous;
      };
    }
  }, [open]);

  const sizeClass =
    size === "sm"
      ? "max-w-md"
      : size === "md"
      ? "max-w-xl"
      : size === "lg"
      ? "max-w-4xl"
      : "max-w-5xl";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 280 }}
            className={`relative w-full ${sizeClass} max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-strong border border-slate-100`}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-100 bg-white/95 backdrop-blur-sm rounded-t-3xl">
              <div>
                <h3 className="font-oswald text-lg lg:text-xl font-semibold text-slate-900">
                  {title}
                </h3>
                {subtitle && (
                  <p className="text-xs text-slate-500 mt-0.5 font-overpass">
                    {subtitle}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-6 lg:p-8">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const FormLabel = ({ children, required, hint }) => (
  <label className="block mb-1.5 text-sm font-semibold text-slate-700 font-overpass">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
    {hint && <span className="ml-2 text-xs font-normal text-slate-400">{hint}</span>}
  </label>
);

export const FormInput = React.forwardRef(({ className = "", ...props }, ref) => (
  <input
    ref={ref}
    {...props}
    className={`w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 focus:bg-white outline-none font-overpass text-slate-800 placeholder:text-slate-400 transition-all ${className}`}
  />
));

export const FormTextarea = React.forwardRef(
  ({ className = "", ...props }, ref) => (
    <textarea
      ref={ref}
      {...props}
      className={`w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 focus:bg-white outline-none font-overpass text-slate-800 placeholder:text-slate-400 transition-all resize-y ${className}`}
    />
  )
);

export const SubmitButton = ({ children, loading, ...props }) => (
  <button
    type="submit"
    disabled={loading}
    {...props}
    className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-brand-600 to-eco-600 text-white font-semibold text-sm shadow-lg shadow-brand-500/30 hover:shadow-brand-500/50 transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
  >
    {loading ? (
      <>
        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        Memproses...
      </>
    ) : (
      <>
        {children}
        <span>→</span>
      </>
    )}
  </button>
);

export const CancelButton = ({ children = "Batal", onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm transition-colors"
  >
    {children}
  </button>
);

export const CharCounter = ({ value, max }) => (
  <div
    className={`text-xs mt-1 text-right font-overpass ${
      value >= max ? "text-red-500 font-semibold" : "text-slate-400"
    }`}
  >
    {value}/{max}
  </div>
);

export default AdminFormModal;
